class SizeInquiryButton extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.isSubmitting = false;
  }

  connectedCallback() {
    this.render();
    this.addEventListeners();
    this.initializeModal();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    const modal = document.querySelector('size-inquiry-form-modal');
    this.cleanupModalEventListeners(modal);
  }

  addEventListeners() {
    this.addEventListener('click', this.handleClick);
  }

  removeEventListeners() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick(event) {
    event.preventDefault();
    this.openSizeInquiryForm();
  }

  render() {
    const buttonText = this.textContent.trim() || "DON'T SEE YOUR SIZE?";
    this.innerHTML = '';
    this.textContent = buttonText;
    this.style.cursor = 'pointer';
    this.classList.add('size-inquiry-button');
  }

  initializeModal() {
    const modal = document.querySelector('size-inquiry-form-modal');
    if (!modal) return;
    
    // Check if modal is already globally initialized to prevent duplicate listeners
    if (!modal.dataset.listenersInitialized) {
      this.setupModalEventListeners();
      modal.dataset.listenersInitialized = 'true';
    }
  }

  setupModalEventListeners() {
    const modal = document.querySelector('size-inquiry-form-modal');
    if (!modal) return;

    // Remove existing listeners to prevent duplicates
    this.cleanupModalEventListeners(modal);

    const closeButton = modal.querySelector('.size-inquiry-form-modal__close-button');
    if (closeButton) {
      this.closeButtonHandler = () => this.hideSizeInquiryModal(modal);
      closeButton.addEventListener('click', this.closeButtonHandler);
    }

    const overlay = modal.querySelector('.size-inquiry-form-modal__overlay');
    if (overlay) {
      this.overlayHandler = () => this.hideSizeInquiryModal(modal);
      overlay.addEventListener('click', this.overlayHandler);
    }

    this.keydownHandler = (e) => {
      if (e.key === 'Escape' && modal.classList.contains('active')) {
        this.hideSizeInquiryModal(modal);
      }
    };
    document.addEventListener('keydown', this.keydownHandler);

    const form = modal.querySelector('#size-inquiry-form');
    if (form) {
      this.formSubmitHandler = (e) => this.handleSizeInquirySubmit(e, modal);
      form.addEventListener('submit', this.formSubmitHandler);
    }
  }

  cleanupModalEventListeners(modal) {
    if (!modal) return;

    const closeButton = modal.querySelector('.size-inquiry-form-modal__close-button');
    if (closeButton && this.closeButtonHandler) {
      closeButton.removeEventListener('click', this.closeButtonHandler);
    }

    const overlay = modal.querySelector('.size-inquiry-form-modal__overlay');
    if (overlay && this.overlayHandler) {
      overlay.removeEventListener('click', this.overlayHandler);
    }

    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
    }

    const form = modal.querySelector('#size-inquiry-form');
    if (form && this.formSubmitHandler) {
      form.removeEventListener('submit', this.formSubmitHandler);
    }
  }

  openSizeInquiryForm() {
    const productTitle = this.getAttribute('data-product-title');
    const productName = this.getAttribute('data-product-name');
    const productColor = this.getAttribute('data-product-color');
    const productId = this.getAttribute('data-product-id');
    const productImage = this.getAttribute('data-product-image');
    const productPrice = this.getAttribute('data-product-price');

    const quickAddDialog = this.closest('quick-add-dialog');
    if (quickAddDialog && typeof quickAddDialog.closeDialog === 'function') {
      quickAddDialog.closeDialog();
    }

    const currentUrl = window.location.href;
    const modal = document.querySelector('size-inquiry-form-modal');

    if (!modal) {
      console.error('Size inquiry modal not found');
      return;
    }

    this.updateSizeInquiryContent(modal, {
      title: productTitle,
      name: productName,
      color: productColor,
      url: currentUrl,
      id: productId,
      image: productImage,
      price: productPrice,
    });

    this.showSizeInquiryModal(modal);
  }

  updateSizeInquiryContent(modal, productData) {
    const productImage = modal.querySelector('.size-inquiry-form-modal__product-image img');
    if (productImage && productData.image) {
      productImage.src = productData.image;
      productImage.alt = productData.name || 'Product Image';
    }

    const productTitle = modal.querySelector('.product-title');
    if (productTitle) {
      productTitle.textContent = productData.name || '';
    }

    const productColor = modal.querySelector('.product-color');
    if (productColor) {
      productColor.textContent = productData.color || '';
    }

    const productPrice = modal.querySelector('.product-price');
    if (productPrice) {
      productPrice.textContent = productData.price || '';
    }

    const modalTitle = modal.querySelector('.size-inquiry-form-modal__title');
    if (modalTitle) {
      modalTitle.textContent = `DON'T SEE YOUR SIZE?`;
    }

    const form = modal.querySelector('#size-inquiry-form');
    if (form) {
      const productTitleInput = form.querySelector('input[name="product_title"]');
      const productUrlInput = form.querySelector('input[name="product_url"]');
      const productIdInput = form.querySelector('input[name="product_id"]');
      const dateTimeInput = form.querySelector('input[name="date_time"]');

      if (productTitleInput) productTitleInput.value = productData.title || '';
      if (productUrlInput) productUrlInput.value = productData.url || '';
      if (productIdInput) productIdInput.value = productData.id || '';

      if (dateTimeInput) {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const formattedDateTime = `${day}/${month}/${year} - ${hours}:${minutes}:${seconds}`;
        dateTimeInput.value = formattedDateTime;
      }

      const requiredSizeInput = form.querySelector('#required_size');
      const customerEmailInput = form.querySelector('#customer_email');

      if (requiredSizeInput) requiredSizeInput.value = '';
      if (customerEmailInput) customerEmailInput.value = '';
    }
  }

  showSizeInquiryModal(modal) {
    modal.classList.add('active');
    const container = modal.querySelector('.size-inquiry-form-modal__container');
    if (container) {
      container.classList.remove('closing');
      container.classList.add('active');
    }
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      const sizeInput = modal.querySelector('#required_size');
      if (sizeInput) sizeInput.focus();
    }, 100);
  }

  hideSizeInquiryModal(modal) {
    const container = modal.querySelector('.size-inquiry-form-modal__container');
    if (container) {
      container.classList.remove('active');
      container.classList.add('closing');
    }
    modal.classList.remove('active');
    document.body.style.overflow = '';
  }

  handleSizeInquirySubmit(event, modal) {
    event.preventDefault();

    if (this.isSubmitting) {
      console.warn('Form already submitting...');
      return;
    }

    const form = event.target;
    const GOOGLE_APPS_SCRIPT_URL =
      'https://script.google.com/macros/s/AKfycbxyqToaiC9uMN2mkRFKdZ9t-g1KePpf7jB_s6-uvPCAKKNaMNL-qeEKKtFZ9g8-hbSmtA/exec';

    const requiredSize = form.querySelector('#required_size').value;
    const customerEmail = form.querySelector('#customer_email').value;

    if (!requiredSize || !customerEmail) {
      this.showSizeInquiryError(form, 'Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      this.showSizeInquiryError(form, 'Please enter a valid email address');
      return;
    }

    this.isSubmitting = true;

    const submitButton = form.querySelector('.custom-button_inner');
    const submitButtonText = form.querySelector('.custom-button_text');

    if (!submitButton) {
      this.isSubmitting = false;
      return;
    }

    const originalInnerText = submitButton.textContent;
    const originalDataText = submitButton.getAttribute('data-text') || 'Submit';
    
    submitButton.dataset.originalInnerText = originalInnerText;
    submitButton.dataset.originalDataText = originalDataText;

    submitButton.setAttribute('data-text', 'Submitting...');
    if (submitButtonText) {
      submitButtonText.textContent = 'Submitting...';
    }
    submitButton.disabled = true;

    const formData = new FormData(form);
    const data = Object.fromEntries(formData);

    fetch(GOOGLE_APPS_SCRIPT_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        formId: 'form1',
        productName: data.product_title,
        productURL: data.product_url,
        productID: data.product_id,
        dateTime: data.date_time,
        requiredSize: data.required_size,
        email: data.customer_email,
      }),
    })
      .then((response) => {
        this.showSizeInquirySuccess(form, modal);
      })
      .catch((error) => {
        console.error('Google Sheets Error:', error);
        this.showSizeInquiryError(form, 'Error occurred. Please try again.');
      })
      .finally(() => {
        this.isSubmitting = false;
      });
  }

  showSizeInquirySuccess(form, modal) {
    const submitButton = form.querySelector('.custom-button_inner');
    const submitButtonText = form.querySelector('.custom-button_text');

    if (submitButton) {
      submitButton.setAttribute('data-text', 'Submitted');
      if (submitButtonText) {
        submitButtonText.textContent = 'Submitted';
      }
    }

    setTimeout(() => {
      this.hideSizeInquiryModal(modal);
      setTimeout(() => {
        if (submitButton) {
          const originalInnerText = submitButton.dataset.originalInnerText || 'Submit';
          const originalDataText = submitButton.dataset.originalDataText || 'Submit';
          
          submitButton.setAttribute('data-text', originalDataText);
          if (submitButtonText) {
            submitButtonText.textContent = originalDataText;
          }
          submitButton.disabled = false;
        }
      }, 300);
    }, 2000);
  }

  showSizeInquiryError(form, message = 'Error occurred') {
    const submitButton = form.querySelector('.custom-button_inner');
    const submitButtonText = form.querySelector('.custom-button_text');
    
    if (submitButton) {
      const originalInnerText = submitButton.dataset.originalInnerText || 'Submit';
      const originalDataText = submitButton.dataset.originalDataText || 'Submit';
      
      submitButton.setAttribute('data-text', message);
      if (submitButtonText) {
        submitButtonText.textContent = message;
      }

      setTimeout(() => {
        submitButton.setAttribute('data-text', originalDataText);
        if (submitButtonText) {
          submitButtonText.textContent = originalDataText;
        }
        submitButton.disabled = false;
      }, 3000);
    }
  }

  get productTitle() {
    return this.getAttribute('data-product-title');
  }

  get productName() {
    return this.getAttribute('data-product-name');
  }

  get productColor() {
    return this.getAttribute('data-product-color');
  }

  get productId() {
    return this.getAttribute('data-product-id');
  }

  get productImage() {
    return this.getAttribute('data-product-image');
  }

  get productPrice() {
    return this.getAttribute('data-product-price');
  }

  set productTitle(value) {
    this.setAttribute('data-product-title', value);
  }

  set productName(value) {
    this.setAttribute('data-product-name', value);
  }

  set productColor(value) {
    this.setAttribute('data-product-color', value);
  }

  set productId(value) {
    this.setAttribute('data-product-id', value);
  }

  set productImage(value) {
    this.setAttribute('data-product-image', value);
  }

  set productPrice(value) {
    this.setAttribute('data-product-price', value);
  }

  updateProductData(productData) {
    if (productData.title) this.productTitle = productData.title;
    if (productData.name) this.productName = productData.name;
    if (productData.color) this.productColor = productData.color;
    if (productData.id) this.productId = productData.id;
    if (productData.image) this.productImage = productData.image;
    if (productData.price) this.productPrice = productData.price;
  }
}

customElements.define('size-inquiry-button', SizeInquiryButton);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const modal = document.querySelector('size-inquiry-form-modal');
    if (modal && !modal.dataset.initialized) {
      modal.dataset.initialized = 'true';
    }
  });
} else {
  const modal = document.querySelector('size-inquiry-form-modal');
  if (modal && !modal.dataset.initialized) {
    modal.dataset.initialized = 'true';
  }
}